import { Typography } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PROJECT_TITLE } from "~app/constants";
import "./PrivacyPage.css";

const GENERAL_PRIVACY_NOTICE_URL = "https://sportradar.com/privacy-notice/";
const PRIVACY_FORM_URL =
  "https://privacyportal-de.onetrust.com/webform/a65e007c-be1f-41dc-93c3-d7020bd23faa/d43c2b96-3b82-4b73-8215-58599caa5086";
const PRIVACY_EMAIL = "privacy@sportradar.com";

const Strong = ({ children }: { children: ReactNode }) => <strong>{children}</strong>;

const PrivacyEmailLink = () => <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>;

const PrivacyNoticeContent = () => (
  <>
    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        1. Introduction
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        The Sportradar Group respects your privacy and is committed to protecting your personal data. This privacy
        notice (<Strong>“Privacy Notice”</Strong>) governs the Sportradar Group’s use of the personal data of team
        managers (“Team Managers”) who access or use the Team Manager App ( “App”) to select existing players to
        participate in an upcoming fixture and submit the selection to Datacore so that it becomes publicly available
        via Datacore API. This Privacy Notice supplements, and should be read together with,{" "}
        <a href={GENERAL_PRIVACY_NOTICE_URL} target="_blank" rel="noopener noreferrer">
          Sportradar’s general Privacy Notice
        </a>
        , which sets out our overarching data protection practices, your rights, and how to contact us.
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        The definition of personal data may vary from country to country. To ensure the protection of your personal
        data, we define it as any information that may directly or indirectly identify you.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        2. Who we are and what we do
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        The Sportradar Group is made up of different legal entities. This Privacy Notice is issued on behalf of
        Sportradar AG (Feldlistrasse 2, CH-9000 St. Gallen, Switzerland, VAT identification number CHE-113.910.142
        MWST), and in it we include information about personal data processing within the different group companies –
        Sportradar subsidiaries and affiliates. In this Privacy Notice, <Strong>“Sportradar”</Strong>,{" "}
        <Strong>“our”</Strong>, <Strong>“we”</Strong> or <Strong>“us”</Strong> refer to Sportradar AG and our
        subsidiaries and affiliates. For the processing described in this Privacy Notice, Sportradar acts as the
        controller of your personal data as a Team Manager accessing or using the App.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        3. What personal data we collect and why
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        When you access or use the App as a Team Manager, we collect and process the following categories of personal
        data to create and provide access to your account in the App:
      </Typography.Paragraph>
      <ul className="PrivacyPage-list">
        <li className="PrivacyPage-listItem">Name</li>
        <li className="PrivacyPage-listItem">Email and password</li>
        <li className="PrivacyPage-listItem">Google sign in information as selected by the Team Manager</li>
      </ul>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        4. Lawful basis for processing
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Sportradar’s lawful basis for processing your personal data as a Team Manager is its necessity for the
        performance of our contract with you and to comply with our legal obligations (for example, recordkeeping or
        responding to lawful requests from authorities). Where any part of the processing described in this Privacy
        Notice requires your consent, we will ask for it separately, and you may withdraw it at any time without
        affecting the lawfulness of processing carried out before its withdrawal.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        5. How we process your personal data
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Sportradar processes your personal data in accordance with the below data protection principles:
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Lawfulness
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        When processing your personal data, we ensure that the processing is carried out in accordance with the
        applicable laws.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Transparency
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        When processing your personal data, we strive to provide you with as much information as possible about the
        collection and processing of your personal data.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Purpose Limitation
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Your personal data is being processed for specified, explicit and legitimate purposes and not further processed
        in a manner that is incompatible with the original purpose.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Data minimization
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We only collect and/or process personal data which is strictly necessary for our services which include
        processing of personal data.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Accuracy
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We keep your personal data as accurate as possible and you may rectify your personal data at any time.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Storage Limitation
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We do not retain your personal data for longer than it is necessary. See also{" "}
        <Strong>Section 6 (How long we keep your personal data)</Strong> below.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Integrity and Confidentiality
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We use appropriate security measures to ensure the protection of your personal data, including protection
        against unauthorised or unlawful processing and against accidental loss, destruction, or damage.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        6. How long we keep your personal data
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Sportradar maintains robust data retention policies for the personal data we process. We delete all data after
        the purposes for which it has been collected are fulfilled (including for the purposes of satisfying any legal
        requirements).
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        In any event, the necessity to retain data is assessed by each business unit within Sportradar (in liaison with
        our dedicated Privacy Team) and takes into account the legal and business purposes relevant to the processing of
        that data and whether we can achieve those purposes through other means, the applicable legal requirements, the
        amount, nature, and sensitivity of the personal data, and the potential risk of harm from unauthorised use or
        accidental disclosure of your personal data.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        7. How we may disclose your personal data
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        In order to provide you with access to the App, we share your personal data with the following categories of
        recipients:
      </Typography.Paragraph>
      <ul className="PrivacyPage-list">
        <li className="PrivacyPage-listItem">
          within the family of companies controlled by Sportradar for internal reasons, primarily for business and
          operational purposes, as well as to their subcontractors or other trusted third parties
        </li>
        <li className="PrivacyPage-listItem">
          if we go through a business transition, such as a merger, acquisition by another company, or sale of all or a
          portion of our assets, your personal data will likely be among the assets transferred
        </li>
        <li className="PrivacyPage-listItem">
          when legally required to do so (e.g., to cooperate with law enforcement investigations or other legal
          proceedings).
        </li>
      </ul>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We may disclose certain personal data to vendors, contractors, or others who assist <Strong>Sportradar</Strong>{" "}
        in delivering the services.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        8. International data transfers
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Certain recipients described in Section 7 above (for example, our cloud-based case management, identity, and
        telephony providers) may process your personal data outside the EEA, United Kingdom, or Switzerland.
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        If your personal data is collected within the EEA or United Kingdom and Switzerland and transferred to countries
        outside the EEA or United Kingdom and Switzerland in which applicable laws do not offer the same level of data
        protection, we ensure similar protection and put in place, for example, one of these safeguards:
      </Typography.Paragraph>
      <ul className="PrivacyPage-list">
        <li className="PrivacyPage-listItem">
          we will transfer your personal data to countries that have been found by the EEA or the United Kingdom to
          provide an adequate level of protection for personal data
        </li>
        <li className="PrivacyPage-listItem">
          we will also use standard contractual clauses approved by the EEA and/or United Kingdom, together with any
          supplementary measures required, with our partners and suppliers that are based in countries outside the EEA
          or United Kingdom and Switzerland and take measures to ensure that our partners and suppliers provide an
          adequate level of data protection. These standard contractual clauses grant your personal data protection
          which is equivalent to the protection it has in the EEA, United Kingdom and Switzerland (as appropriate).
        </li>
      </ul>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Should you require further information regarding the relevant data transfer safeguards that we have implemented,
        then please reach out to us as provided under <Strong>Section 14 (How to contact us)</Strong>.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        9. What data protection rights you have
      </Typography.Title>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to access (know)
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You have the right to obtain from us a confirmation as to whether we process your personal data. Should we
        process your personal data, you have the right to request access to your personal data and to obtain further
        information regarding the processing of your personal data.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to rectification (correction)
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You have the right to have your personal data rectified in the event that it is inaccurate. You can also rectify
        some of your personal data by reviewing and changing the personal data from your account settings.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to erasure (deletion)
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Under certain circumstances, you have the right to request that we delete your personal data.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to restriction of processing
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Under certain circumstances, you have the right to restrict the processing of your personal data.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to object to processing (right to opt out)
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Under certain circumstances, you have the right to object to the processing of your personal data. You can
        exercise your right, by completing{" "}
        <a href={PRIVACY_FORM_URL} target="_blank" rel="noopener noreferrer">
          this form
        </a>
        .
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to data portability
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You have the right to obtain your personal data in a structured, commonly used, and machine-readable format and
        to have your personal data transferred to another controller, where technically feasible and insofar as the
        processing of your personal data is based on your consent or is necessary for the performance of a contract
        between us.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right not to be subject to a decision based solely on automated processing, including profiling
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You have the right not to be subject to a decision based solely on automated processing, including profiling,
        which produces legal effects concerning you or similarly significantly affects you.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to complain to a supervisory authority
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        If you are dissatisfied with how we have handled your request, or if you believe that we have not adequately
        addressed your data protection rights, you have the right to lodge a complaint with us.
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Should you believe that we have violated any applicable data protection laws when processing your personal data,
        you have the right to file a complaint with the responsible Data Protection Authority.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Right to withdraw consent
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You may withdraw your consent to the processing of your personal data insofar as the processing is based on your
        consent. This does not apply if the processing of your personal data is required for the performance of a
        contract between us or if the processing is based on our legitimate interests.
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        You, or your authorized agent, can exercise your rights by submitting{" "}
        <a href={PRIVACY_FORM_URL} target="_blank" rel="noopener noreferrer">
          this form
        </a>
        .
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We reserve the right to refuse to give effect to a request to exercise any of the above data protection rights
        if we are unable to ascertain your identity.
      </Typography.Paragraph>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        The details of the lead Data Protection Authority that Sportradar AG is registered with are as follows:
      </Typography.Paragraph>
      <div className="PrivacyPage-authorityBlock">
        <Typography.Text>Austrian Data Protection Authority</Typography.Text>
        <Typography.Text>Österreichische Datenschutzbehörde</Typography.Text>
        <Typography.Text>Barichgasse 40-42, 1030 Vienna, Austria</Typography.Text>
        <Typography.Text>Telephone: +43 1 52 152-0</Typography.Text>
        <Typography.Text>
          E-Mail: <a href="mailto:dsb@dsb.gv.at">dsb@dsb.gv.at</a>
        </Typography.Text>
      </div>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        10. Security of your personal data
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Sportradar has implemented appropriate technical and organisational measures to ensure a level of security
        appropriate to the risk involved in order to protect your personal data against a breach of security leading to
        the accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to, your
        personal data transmitted, stored, or otherwise processed. Our employees are required to keep personal data
        confidential.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        11. Non-discrimination
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        We will not discriminate against you for exercising any of your rights. We will not:
      </Typography.Paragraph>
      <ul className="PrivacyPage-list">
        <li className="PrivacyPage-listItem">deny you goods or services</li>
        <li className="PrivacyPage-listItem">
          charge you different prices or rates for goods or services, including through granting discounts or other
          benefits, or imposing penalties
        </li>
        <li className="PrivacyPage-listItem">provide you a different level or quality of goods or service</li>
        <li className="PrivacyPage-listItem">
          suggest that you may receive a different price or rate for goods or services or a different level or quality
          of goods or services
        </li>
      </ul>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        12. United States Consumers
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        In addition to the rights outlined in Section 10 of this Privacy Notice, U.S. consumers (
        <Strong>“Consumer”</Strong>) have certain rights regarding their personal information.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Do Not Sell or Share My Personal Information
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Based on your state of residence, you may have the right to opt out of the sale or sharing of your personal
        information. If you wish to opt out of the sale or sharing of your personal information, please complete{" "}
        <a href={PRIVACY_FORM_URL} target="_blank" rel="noopener noreferrer">
          this form
        </a>{" "}
        to facilitate your request to opt out of sale/sharing. We will use the information you provide to handle your
        inquiry and keep a record of both your request and our corresponding response.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle">
        Appealing decisions regarding your personal information
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Depending on your state of residence, you may have the right to appeal our decision about your request regarding
        your personal information. To exercise your right to appeal a decision, please send your request to{" "}
        <PrivacyEmailLink /> or complete{" "}
        <a href={PRIVACY_FORM_URL} target="_blank" rel="noopener noreferrer">
          this form
        </a>
        .
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        13. How This Privacy Notice May Change
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Please note that this Privacy Notice shall be updated at least once every 12 months. We will place a prominent
        notice that will be visible on the Team Manager App. You should check back here periodically to see if the
        Privacy Notice has been updated. We will always show the date of the latest modification of the Privacy Notice
        at the top of the page so you can tell when it was last revised.
      </Typography.Paragraph>
    </section>

    <section className="PrivacyPage-section">
      <Typography.Title level={4} className="PrivacyPage-sectionTitle">
        14. How to contact us
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        Should you have any questions regarding the processing of your personal data or should you wish to exercise your
        rights under this Privacy Notice, please submit your request via{" "}
        <a href={PRIVACY_FORM_URL} target="_blank" rel="noopener noreferrer">
          this form
        </a>{" "}
        or contact our Data Protection Office via email at <PrivacyEmailLink />.
      </Typography.Paragraph>
      <Typography.Title level={5} className="PrivacyPage-subsectionTitle" style={{ color: "red" }}>
        [APPLICABLE ONLY IF YOU ARE A RESIDENT OF THE STATE OF CALIFORNIA (CALIFORNIA CONSUMER)]
      </Typography.Title>
      <Typography.Paragraph className="PrivacyPage-paragraph">
        In addition to the methods available above for exercising any of your rights, please note that you can submit a
        verifiable request also by calling us at <Strong>1 888 297 1323</Strong> (toll-free).
      </Typography.Paragraph>
    </section>
  </>
);

export const PrivacyPage = () => (
  <div className="PrivacyPage">
    <header className="PrivacyPage-header">
      <Link to="/" className="PrivacyPage-headerLink">
        <img className="PrivacyPage-logo" src="/img/SR_logo_pos.png" alt="Sportradar" />
        <Typography.Text className="PrivacyPage-headerTitle">{PROJECT_TITLE}</Typography.Text>
      </Link>
    </header>

    <main className="PrivacyPage-content">
      <Typography.Title level={2} className="PrivacyPage-title">
        Team Manager App Privacy Notice
      </Typography.Title>
      <Typography.Text className="PrivacyPage-lastUpdated">Last updated: August 2026.</Typography.Text>

      <PrivacyNoticeContent />
    </main>
  </div>
);
